import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button, Space, Typography, Tooltip, Tag, message } from 'antd';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../interfaces/Global';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  CloseOutlined,
  FileProtectOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { ISlidePropostaData } from '../interface';
import { SlideCapa } from './SlideCapa';
import { SlideMunicipios } from './SlideMunicipios';
import { SlideObjeto } from './SlideObjeto';
import { SlideBeneficios } from './SlideBeneficios';
import { SlideTela } from './SlideTela';
import { SlideTotem } from './SlideTotem';
import { SlideValores } from './SlideValores';
import { SlideContato } from './SlideContato';
import { PropostaPrintDocument } from './PropostaPrintDocument';
import { gerarPdfPropostaDireto } from '../utils/gerarPdfProposta';

const { Text, Title } = Typography;

export interface VisualizadorPropostaModalProps {
  visible: boolean;
  onClose: () => void;
  data?: ISlidePropostaData | null;
  title?: string;
  podeBaixarPdf?: boolean;
}

export const VisualizadorPropostaModal: React.FC<VisualizadorPropostaModalProps> = ({
  visible,
  onClose,
  data,
  title,
  podeBaixarPdf = true
}) => {
  const isDark = useSelector((state: stateGlobal) => state?.global?.theme === 'dark');
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [customScale, setCustomScale] = useState<number | null>(null);
  const [gerandoPdf, setGerandoPdf] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  
  const printRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<(HTMLElement | null)[]>([]);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const docTitle = useMemo(() => {
    const muni = (data?.cidade_nome || 'Municipio').replace(/\s+/g, '_');
    const uf = data?.cidade_uf ? `_${data.cidade_uf.toUpperCase()}` : '';
    return `Proposta_Comercial_${muni}${uf}_GovFacil`;
  }, [data]);

  const handleDownloadPdf = async () => {
    if (!printRef.current) return;
    setGerandoPdf(true);
    message.loading({ content: 'Gerando PDF da apresentação...', key: 'pdf_gen', duration: 0 });
    try {
      await gerarPdfPropostaDireto({
        containerElement: printRef.current,
        documentTitle: docTitle,
        onProgress: (current, total) => {
          message.loading({ content: `Processando slide ${current} de ${total}...`, key: 'pdf_gen', duration: 0 });
        }
      });
      message.success({ content: 'PDF gerado e baixado com sucesso!', key: 'pdf_gen', duration: 3 });
    } catch (error) {
      console.error(error);
      message.error({ content: 'Erro ao gerar o PDF da proposta', key: 'pdf_gen', duration: 3 });
    } finally {
      setGerandoPdf(false);
    }
  };

  const calculateAutoFitScale = useCallback(() => {
    const mobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    if (mobile) {
      const availWidth = Math.max(window.innerWidth - 18, 280);
      return availWidth / 1920;
    }
    const availWidth = Math.min(window.innerWidth * 0.88, 1400);
    const availHeight = window.innerHeight * 0.76;
    const scaleX = availWidth / 1920;
    const scaleY = availHeight / 1080;
    return Math.min(scaleX, scaleY, 0.72);
  }, []);

  const [autoScale, setAutoScale] = useState<number>(() => calculateAutoFitScale());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setAutoScale(calculateAutoFitScale());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateAutoFitScale]);

  useEffect(() => {
    if (visible) {
      setCurrentSlideIndex(0);
      setCustomScale(null);
      setIsMobile(window.innerWidth <= 768);
      setAutoScale(calculateAutoFitScale());
    }
  }, [visible, calculateAutoFitScale]);

  const activeScale = customScale ?? autoScale;

  const slides = useMemo(() => {
    const list = [
      { id: 'capa', title: 'Capa', component: <SlideCapa data={data || undefined} scale={activeScale} /> },
      { id: 'municipios', title: 'Institucional', component: <SlideMunicipios data={data || undefined} scale={activeScale} /> },
      { id: 'beneficios', title: 'Benefícios', component: <SlideBeneficios data={data || undefined} scale={activeScale} /> },
      { id: 'objeto', title: 'Objeto', component: <SlideObjeto data={data || undefined} scale={activeScale} /> },
    ];

    list.push({ id: 'valores', title: 'Valores e Condições', component: <SlideValores data={data || undefined} scale={activeScale} /> });

    if (data?.possui_tela !== false) {
      if (data?.tipo_tela === 'totem') {
        list.push({ id: 'totem', title: 'Totem Interativo 32"', component: <SlideTotem data={data || undefined} scale={activeScale} /> });
      } else if (data?.tipo_tela === 'tela85') {
        list.push({ id: 'tela85', title: 'Tela Interativa 85"', component: <SlideTela data={data || undefined} scale={activeScale} /> });
      } else {
        list.push({ id: 'tela', title: 'Tela Interativa 75"', component: <SlideTela data={data || undefined} scale={activeScale} /> });
      }
    }

    list.push({ id: 'contato', title: 'Contato', component: <SlideContato data={data || undefined} scale={activeScale} /> });

    return list;
  }, [data, activeScale]);

  const totalSlides = slides.length;

  const handleNext = useCallback(() => {
    setCurrentSlideIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : 0));
  }, [totalSlides]);

  const handlePrev = useCallback(() => {
    setCurrentSlideIndex((prev) => (prev > 0 ? prev - 1 : totalSlides - 1));
  }, [totalSlides]);

  useEffect(() => {
    if (isMobile && pillRefs.current[currentSlideIndex]) {
      pillRefs.current[currentSlideIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [currentSlideIndex, isMobile]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      touchStartXRef.current = e.touches[0].clientX;
      touchStartYRef.current = e.touches[0].clientY;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;
  
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, handleNext, handlePrev, onClose]);

  const handleZoomIn = () => {
    setCustomScale((prev) => Math.min((prev ?? autoScale) + 0.05, 1.0));
  };

  const handleZoomOut = () => {
    setCustomScale((prev) => Math.max((prev ?? autoScale) - 0.05, 0.25));
  };

  const handleResetZoom = () => {
    setCustomScale(null);
    setAutoScale(calculateAutoFitScale());
  };

  const cidadeFormatada = data?.cidade_nome
    ? `${data.cidade_nome}${data.cidade_uf ? ` - ${data.cidade_uf}` : ''}`
    : 'Município';

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '98vw' : '92vw'}
      closable={false}
      style={{
        top: isMobile ? 6 : 15,
        margin: isMobile ? '0 auto' : undefined,
        maxWidth: isMobile ? '100vw' : '1600px',
        padding: 0,
      }}
      bodyStyle={{
        padding: isMobile ? '8px 8px 12px 8px' : '12px 20px 20px 20px',
        background: isDark ? '#1b1b1b' : '#f0f2f5',
        borderRadius: isMobile ? '12px' : '10px',
      }}
      destroyOnClose
    >
      {/* Visualização Desktop */}
      {!isMobile ? (
        <>
          {/* Barra Superior Desktop com Controles */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: 12,
              borderBottom: isDark ? '1px solid #303030' : '1px solid #e2e8f0',
              marginBottom: 14,
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <Space align="center">
              <FileProtectOutlined style={{ fontSize: 22, color: '#0066b2' }} />
              <div>
                <Title level={4} style={{ margin: 0, color: isDark ? '#ffffff' : '#1e293b', fontSize: 17 }}>
                  {title || `Apresentação da Proposta Comercial: ${cidadeFormatada}`}
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {data?.plano_nome ? `Modelo: ${data.plano_nome}` : 'Apresentação Comercial'} • Use as setas do teclado para navegar
                </Text>
              </div>
            </Space>

            <Space size="middle" wrap>
              {/* Botão de Exportar / Baixar PDF */}
              <Tooltip title={!podeBaixarPdf ? "Salve a proposta antes de baixar o PDF" : undefined}>
                <Button
                  type="primary"
                  danger
                  icon={<FilePdfOutlined />}
                  onClick={handleDownloadPdf}
                  loading={gerandoPdf}
                  disabled={!podeBaixarPdf || gerandoPdf}
                  size="middle"
                  style={{ fontWeight: 600, boxShadow: !podeBaixarPdf ? 'none' : '0 2px 6px rgba(239, 68, 68, 0.3)' }}
                >
                  {gerandoPdf ? 'Gerando PDF...' : 'Baixar PDF'}
                </Button>
              </Tooltip>

              {/* Contador de Slides */}
              <Tag color="blue" style={{ fontSize: 13, padding: '3px 10px', borderRadius: 12, fontWeight: 600 }}>
                Slide {currentSlideIndex + 1} de {totalSlides} : {slides[currentSlideIndex]?.title}
              </Tag>

              {/* Navegação Anterior / Próximo */}
              <Space>
                <Button icon={<LeftOutlined />} onClick={handlePrev} size="middle">
                  Anterior
                </Button>
                <Button type="primary" icon={<RightOutlined />} onClick={handleNext} size="middle">
                  Próximo
                </Button>
              </Space>

              {/* Controles de Zoom */}
              <Space size="small">
                <Tooltip title="Diminuir Zoom">
                  <Button icon={<ZoomOutOutlined />} size="small" onClick={handleZoomOut} />
                </Tooltip>
                <Text style={{ fontSize: 11, minWidth: 42, textAlign: 'center', color: isDark ? '#ffffffd9' : '#334155' }}>
                  {Math.round(activeScale * 100)}%
                </Text>
                <Tooltip title="Aumentar Zoom">
                  <Button icon={<ZoomInOutlined />} size="small" onClick={handleZoomIn} />
                </Tooltip>
                <Tooltip title="Ajustar à Tela">
                  <Button icon={<FullscreenOutlined />} size="small" onClick={handleResetZoom}>
                    Ajustar
                  </Button>
                </Tooltip>
              </Space>

              <Tooltip title="Fechar">
                <Button
                  danger
                  icon={<CloseOutlined />}
                  size="middle"
                  onClick={onClose}
                  style={{ marginLeft: 8 }}
                />
              </Tooltip>
            </Space>
          </div>

          {/* Área do Slide Desktop */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '480px',
              overflow: 'auto',
              background: isDark ? '#141414' : '#e2e8f0',
              padding: '16px 8px',
              borderRadius: '8px',
              boxShadow: isDark ? 'inset 0 2px 6px rgba(0,0,0,0.6)' : 'inset 0 2px 4px rgba(0,0,0,0.06)',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            {slides.map((slide, idx) => (
              <div
                key={slide.id}
                style={{
                  display: idx === currentSlideIndex ? 'flex' : 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {slide.component}
              </div>
            ))}
          </div>

          {/* Pílulas de Navegação Desktop */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 14,
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Space size={6} wrap style={{ justifyContent: 'center' }}>
              {slides.map((s, idx) => {
                const isCurrent = idx === currentSlideIndex;
                return (
                  <Button
                    key={s.id}
                    size="small"
                    type={isCurrent ? 'primary' : 'default'}
                    onClick={() => setCurrentSlideIndex(idx)}
                    style={{
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: isCurrent ? 700 : 500,
                      height: 28,
                      padding: '0 12px',
                      boxShadow: isCurrent ? '0 2px 6px rgba(0,102,178,0.3)' : 'none',
                    }}
                  >
                    {idx + 1}. {s.title}
                  </Button>
                );
              })}
            </Space>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {/* Top Bar Mobile: Slide Title + PDF + Close */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: 8,
              borderBottom: isDark ? '1px solid #303030' : '1px solid #e2e8f0',
              marginBottom: 8,
              gap: 6,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <Tag
                color="blue"
                style={{
                  fontSize: 12,
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontWeight: 600,
                  margin: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
              >
                {currentSlideIndex + 1}/{totalSlides}: {slides[currentSlideIndex]?.title}
              </Tag>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <Button
                type="primary"
                danger
                icon={<FilePdfOutlined />}
                onClick={handleDownloadPdf}
                loading={gerandoPdf}
                disabled={!podeBaixarPdf || gerandoPdf}
                size="small"
                style={{
                  fontWeight: 600,
                  fontSize: 12,
                  height: 28,
                  padding: '0 8px',
                  borderRadius: 6,
                }}
              >
                {gerandoPdf ? 'PDF...' : 'PDF'}
              </Button>

              <Button
                icon={<CloseOutlined />}
                size="small"
                onClick={onClose}
                style={{
                  height: 28,
                  width: 28,
                  padding: 0,
                  borderRadius: 6,
                }}
              />
            </div>
          </div>

          {/* Barra de Navegação Rápida Mobile */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
              gap: 8,
            }}
          >
            <Button
              icon={<LeftOutlined />}
              size="middle"
              onClick={handlePrev}
              style={{
                flex: 1,
                borderRadius: 8,
                fontWeight: 600,
                height: 34,
                fontSize: 13,
              }}
            >
              Anterior
            </Button>

            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: isDark ? '#94a3b8' : '#475569',
                padding: '0 4px',
                whiteSpace: 'nowrap',
              }}
            >
              {currentSlideIndex + 1} / {totalSlides}
            </div>

            <Button
              type="primary"
              icon={<RightOutlined />}
              size="middle"
              onClick={handleNext}
              style={{
                flex: 1,
                borderRadius: 8,
                fontWeight: 600,
                height: 34,
                fontSize: 13,
              }}
            >
              Próximo
            </Button>
          </div>

          {/* Área do Slide Mobile com Suporte a Swipe */}
          <div
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              minHeight: 'auto',
              background: isDark ? '#141414' : '#e2e8f0',
              padding: '6px 2px',
              borderRadius: '8px',
              boxShadow: isDark ? 'inset 0 2px 6px rgba(0,0,0,0.6)' : 'inset 0 2px 4px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              userSelect: 'none',
              touchAction: 'pan-y',
            }}
          >
            {slides.map((slide, idx) => (
              <div
                key={slide.id}
                style={{
                  display: idx === currentSlideIndex ? 'flex' : 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                {slide.component}
              </div>
            ))}
          </div>

          {/* Dica de Swipe */}
          <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 2 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Deslize para os lados para trocar de slide
            </Text>
          </div>

          {/* Carrossel de Pílulas Horizontais Mobile */}
          <div
            style={{
              display: 'flex',
              overflowX: 'auto',
              gap: 6,
              padding: '6px 2px 4px 2px',
              width: '100%',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {slides.map((s, idx) => {
              const isCurrent = idx === currentSlideIndex;
              return (
                <button
                  key={s.id}
                  ref={(el) => (pillRefs.current[idx] = el)}
                  onClick={() => setCurrentSlideIndex(idx)}
                  style={{
                    flexShrink: 0,
                    borderRadius: 14,
                    fontSize: 12,
                    fontWeight: isCurrent ? 700 : 500,
                    height: 28,
                    padding: '0 12px',
                    border: 'none',
                    cursor: 'pointer',
                    background: isCurrent
                      ? '#1890ff'
                      : isDark
                      ? '#2a2a2a'
                      : '#e2e8f0',
                    color: isCurrent ? '#ffffff' : isDark ? '#d1d5db' : '#334155',
                    boxShadow: isCurrent ? '0 2px 6px rgba(24,144,255,0.4)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {idx + 1}. {s.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Documento Oculto para Impressão e Download do PDF */}
      <PropostaPrintDocument ref={printRef} data={data} />
    </Modal>
  );
};

